import apiClient from '@/shared/services/apiClient';
import type { PaginatedResponse, Post, Tag, Category, Comment, Series } from '@/shared/types/api';

interface ListPostsParams {
  page?: number;
  perPage?: number;
  tag?: number;
  category?: number;
}

export async function listPosts(params: ListPostsParams = {}): Promise<PaginatedResponse<Post>> {
  const { page = 1, perPage = 10, tag, category } = params;
  const queryParams: Record<string, string | number> = { page, per_page: perPage };
  if (tag) queryParams.tag = tag;
  if (category) queryParams.category = category;
  const res = await apiClient.get('/blog/posts', { params: queryParams });
  return res.data;
}

export async function getPost(id: number): Promise<Post> {
  const res = await apiClient.get(`/blog/posts/${id}`);
  return res.data;
}

export async function getTags(): Promise<Tag[]> {
  const res = await apiClient.get('/blog/tags');
  return res.data.tags;
}

export async function getCategories(): Promise<Category[]> {
  const res = await apiClient.get('/blog/categories');
  return res.data.categories;
}

export async function getComments(postId: number): Promise<Comment[]> {
  const res = await apiClient.get(`/blog/posts/${postId}/comments`);
  return res.data.comments;
}

export interface SearchResponse {
  posts: Post[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  query: string;
}

export async function searchPosts(query: string, page = 1): Promise<SearchResponse> {
  const res = await apiClient.get('/blog/search', { params: { q: query, page } });
  return res.data;
}

export async function addComment(
  postId: number,
  data: { author_name?: string; author_email?: string; body: string; parent_id?: number }
): Promise<Comment> {
  const res = await apiClient.post(`/blog/posts/${postId}/comments`, data);
  return res.data;
}

export async function getRelatedPosts(postId: number): Promise<Post[]> {
  const res = await apiClient.get(`/blog/posts/${postId}/related`);
  return res.data.posts;
}

export async function getPostViews(postId: number): Promise<{ views: number }> {
  const res = await apiClient.get(`/blog/posts/${postId}/views`);
  return res.data;
}

export async function getAdjacentPosts(postId: number): Promise<{ prev: Post | null; next: Post | null }> {
  const res = await apiClient.get(`/blog/posts/${postId}/adjacent`);
  return res.data;
}

export async function getPopularPosts(limit = 5): Promise<Post[]> {
  const res = await apiClient.get('/blog/posts/popular', { params: { limit } });
  return res.data.posts;
}

export async function getSeries(): Promise<Series[]> {
  const res = await apiClient.get('/blog/series');
  return res.data.series;
}

export async function getSeriesDetail(id: number): Promise<{ series: Series; posts: Post[] }> {
  const res = await apiClient.get(`/blog/series/${id}`);
  return res.data;
}

export interface PublicSiteSettings {
  blog_maintenance_mode: boolean;
  blog_maintenance_title: string;
  blog_maintenance_text: string;
}

export async function getPublicSiteSettings(): Promise<PublicSiteSettings> {
  const res = await apiClient.get('/blog/site-settings');
  return res.data;
}
